import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation31() {
  const { lesson, course, accent } = findLesson("piano-foundation", "31");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>Group the chords by quality, not by scale order. <strong>1-4-5 are the major group</strong>; <strong>2-3-6 are the minor group</strong>; 7 is diminished (rare).</Highlight>
        <Highlight>Practise <strong>1-4-5 in root position</strong>, then in <strong>first inversion</strong>, then in <strong>second inversion</strong>. Same chords, three textures.</Highlight>
        <Highlight>Then 2-3-6 in the same three positions. Six chords total in the practice routine.</Highlight>
        <Highlight>Then use inversions <strong>functionally</strong> — pick the inversion that makes the chord change <em>smooth</em>. Don't just walk the scale; make music.</Highlight>
        <Highlight>The left hand always plays the chord's <strong>true root</strong>. The right hand can use any inversion; the LH always plays the chord's name.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Group by quality" title="Don't walk the scale — gather the chords" accent={accent.hex}>
        <Pull>
          In this particular part what I would recommend you to do is again do the same
          thing but gang up your chords by quality or by emotion or by function as we
          sometimes call it.
        </Pull>

        <p>
          Part 1 walked through every chord in scale order. That's the linear drill. Part 2
          reorganises the same chords into musically meaningful groups — the major chords
          together, the minor chords together — so you can practise them as <em>families</em>
          rather than as a sequence.
        </p>
      </Insight>

      <Insight eyebrow="The major group · 1-4-5" title="Three majors, three positions, three patterns" accent={accent.hex}>
        <p>
          In G major, the 1-4-5 chords are <strong>G major, C major, D major</strong>. Cycle
          them in any pattern that uses all three:
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          1 - 4 - 5 - 4 (G - C - D - C)<br />
          5 - 4 - 1 - 4 (D - C - G - C)<br />
          1 - 5 - 4 - 1 (G - D - C - G)
        </p>

        <Steps>
          <Step>
            All three chords in <strong>root position</strong>. Loop them in your chosen
            order. RH plays the chord; LH plays the chord's root.
          </Step>
          <Step>
            All three chords in <strong>first inversion</strong>. G becomes B-D-G; C becomes
            E-G-C; D becomes F♯-A-D. Same cycle, new shapes.
          </Step>
          <Step>
            All three in <strong>second inversion</strong>. G becomes D-G-B; C becomes G-C-E;
            D becomes A-D-F♯.
          </Step>
        </Steps>

        <Tip>
          Mix in the rhythm patterns from Part 1 — broken chords, arpeggios, block chords —
          across each inversion. Same chord shapes, different deliveries.
        </Tip>
      </Insight>

      <Insight eyebrow="The minor group · 2-3-6" title="Three minors, same three positions" accent={accent.hex}>
        <p>
          In G major, the minor chords are <strong>A minor, B minor, E minor</strong>. Same
          three-inversion drill. First, all three in root position. Then all three in first
          inversion. Then all three in second inversion.
        </p>

        <p>
          The minor group has its own emotional character — gather the three of them and
          you've got a vocabulary for the "introspective" sections of any song in G major.
          The 1-4-5 majors give you the bright sections; the 2-3-6 minors give you the
          shadowed ones.
        </p>
      </Insight>

      <Insight eyebrow="Functional inversion choice" title="Pick the inversion that makes the change smooth" accent={accent.hex}>
        <Pull>
          You are choosing the most convenient inversion and that's your next practice.
        </Pull>

        <p>
          Once each inversion drills cleanly in isolation, the next stage is to choose the
          <em> right</em> inversion based on what comes next. Go from 1 (G major root
          position: G-B-D) to 4 (C major). The smoothest path is G-C-E — second inversion
          of C — because the G stays put. From 4 back to 1, you might use root-position G.
          From 1 to 5 (D major), pick the inversion that moves the smallest distance.
        </p>

        <p>
          That's the same common-note logic Lesson 18 introduced. Part 2 is where you
          start applying it musically across whole progressions.
        </p>

        <Steps>
          <Step><strong>Bar 1</strong> — G major, root position (G-B-D).</Step>
          <Step><strong>Bar 2</strong> — C major, second inversion (G-C-E). Common note: G.</Step>
          <Step><strong>Bar 3</strong> — back to G major, root (G-B-D).</Step>
          <Step><strong>Bar 4</strong> — D major, first inversion (F♯-A-D). The change to D feels small.</Step>
          <Step><strong>Loop.</strong> Same progression, but now the right hand barely moves.</Step>
        </Steps>
      </Insight>

      <Insight eyebrow="Major to minor instantly" title="Drop all the 3rds — and you're in G minor" accent={accent.hex}>
        <p>
          A small but powerful trick at the end of the lesson: take the same 1-4-5 group
          and turn every chord minor. G becomes G minor (G-B♭-D). C becomes C minor
          (C-E♭-G). D becomes D minor (D-F-A). Same three-chord drill, completely different
          mood. The scale is no longer G major — it's G minor, sharing the same root.
        </p>

        <KeyIdea>
          1-4-5 in major + 1-4-5 in minor = six chord progressions for the price of one
          drill. Both share the same hand position; only the "third" of each chord shifts.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The bigger point" title="Inversions are math you can master" accent={accent.hex}>
        <Pull>
          There are a few things on the piano where it's tough because the creativity
          factor is too much. So you have to kind of keep searching and exploring, but
          inversions are the maths behind the chord. So there are three shapes. Your job
          is to master them. So this is something you should and can actually do.
        </Pull>

        <p>
          Unlike improvisation or composition — which are open-ended forever — inversions
          are a closed problem. Three shapes per chord. Twelve roots. Thirty-six positions
          to internalise. After that, the system is complete. Inversions are one of the
          rare areas of the piano where "mastery" is a definite word.
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-32"
        label="Lesson 32 · Basic Finger Independence (Part 1)"
        why="Chord theory and inversions are mostly mental. The next two lessons swing back to physical training — finger independence drills that prepare your hands for everything ahead."
      />
    </LessonShell>
  );
}
